import { useAuthStore } from "@/stores/authStore";

export type ClientRole = "owner" | "branch_manager" | "staff";

export type ClientFeatureKey =
  | "dashboard"
  | "orders"
  | "lorryReceipts"
  | "tracking"
  | "notifications"
  | "profile"
  | "invoices"
  | "payments"
  | "documents"
  | "team"
  | "mis"
  | "vehicleHireContracts";

// Single source of truth for client-portal tier access.
// Sidebar.tsx and router/index.tsx both read from this — do not
// duplicate these role arrays anywhere else (see Day 85/86 admin
// RBAC lessons: three separate hardcoded lists drifted out of sync).
export const ACCESS_TABLE: Record<ClientFeatureKey, ClientRole[]> = {
  dashboard: ["owner", "branch_manager", "staff"],
  orders: ["owner", "branch_manager", "staff"],
  lorryReceipts: ["owner", "branch_manager", "staff"],
  tracking: ["owner", "branch_manager", "staff"],
  notifications: ["owner", "branch_manager", "staff"],
  profile: ["owner", "branch_manager", "staff"],
  invoices: ["owner", "branch_manager"],
  payments: ["owner", "branch_manager"],
  documents: ["owner", "branch_manager"],
  team: ["owner", "branch_manager"],
  mis: ["owner", "branch_manager"],
  vehicleHireContracts: ["owner", "branch_manager"],
};

export function useClientPermissions() {
  const user = useAuthStore((s) => s.user);

  // Non-client platform roles (admin/super_admin/etc.) aren't subject to
  // this table at all — they have their own separate access model.
  const clientRole = user?.role === "client" ? user.clientRole : undefined;

  function can(feature: ClientFeatureKey): boolean {
    if (!clientRole) return false;
    return ACCESS_TABLE[feature].includes(clientRole);
  }

  return { clientRole, can };
}

// Day 87 complete
